import { useEffect, useState, useRef } from 'react';
import ReactDOM from 'react-dom';

/**
 * PiPCoachingWindow
 * Renders a compact, always-on-top coaching window using the Document
 * Picture-in-Picture API. Falls back to window.open() when the native
 * PiP API is unavailable (Firefox / Safari / older Chrome).
 *
 * Live-syncs props from the parent Dashboard via ReactDOM.createPortal,
 * so analysis, scores and call state update in real-time.
 */
export function PiPCoachingWindow({
  isOpen,
  onClose,
  analysis,
  isAnalyzing,
  isListening,
  callEnded,
  inputMode,
  meetingBotStatus,
  onTogglePause,
  onEndCall,
  prospectName,
  companyName,
  categories,
  pinnedQuestion,
  onPinQuestion,
}) {
  const [container, setContainer] = useState(null);
  const winRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;

    let cancelled = false;
    let pipWin = null;

    const openWindow = async () => {
      // 1) Try native Document Picture-in-Picture API
      if (
        'documentPictureInPicture' in window &&
        typeof window.documentPictureInPicture.requestWindow === 'function'
      ) {
        try {
          pipWin = await window.documentPictureInPicture.requestWindow({
            width: 380,
            height: 520,
          });
        } catch (e) {
          // permission denied or unsupported — will fall through to popup
          // eslint-disable-next-line no-console
          console.warn('Document PiP unavailable, falling back to popup', e);
        }
      }

      // 2) Fallback to a regular popup window
      if (!pipWin) {
        pipWin = window.open(
          '',
          'PipeGuideCoach',
          'width=380,height=520,resizable=yes,scrollbars=no,status=no,location=no,toolbar=no,menubar=no'
        );
        if (!pipWin) {
          // popup blocked
          onClose && onClose();
          return;
        }
      }

      if (cancelled) {
        try { pipWin.close(); } catch (_e) { /* ignore */ }
        return;
      }

      // Set up the PiP document
      try {
        pipWin.document.title = 'PipeGuide Coach';
      } catch (_e) { /* some browsers restrict */ }

      // Copy stylesheets/styles from main document so Tailwind classes work
      try {
        document.querySelectorAll('link[rel="stylesheet"], style').forEach((node) => {
          pipWin.document.head.appendChild(node.cloneNode(true));
        });
      } catch (_e) { /* ignore */ }

      // Inject PiP-only animations (keyframes that PiPContent references)
      try {
        const animStyle = pipWin.document.createElement('style');
        animStyle.textContent = `
          @keyframes pip-pulse {
            0%, 100% { opacity: 1; transform: scale(1); }
            50% { opacity: 0.55; transform: scale(0.85); }
          }
        `;
        pipWin.document.head.appendChild(animStyle);
      } catch (_e) { /* ignore */ }

      // Base body styles
      try {
        pipWin.document.body.style.margin = '0';
        pipWin.document.body.style.padding = '0';
        pipWin.document.body.style.background = '#ffffff';
        pipWin.document.body.style.fontFamily =
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';
        pipWin.document.body.style.color = '#18181b';
        pipWin.document.body.style.overflow = 'hidden';
      } catch (_e) { /* ignore */ }

      // Create mount node
      const mount = pipWin.document.createElement('div');
      mount.id = 'pip-root';
      pipWin.document.body.appendChild(mount);

      // Close hook — handle both APIs
      const handleClose = () => {
        onClose && onClose();
      };
      pipWin.addEventListener('pagehide', handleClose);
      pipWin.addEventListener('unload', handleClose);

      winRef.current = pipWin;
      setContainer(mount);
    };

    openWindow();

    return () => {
      cancelled = true;
      if (winRef.current) {
        try { winRef.current.close(); } catch (_e) { /* ignore */ }
        winRef.current = null;
      }
      setContainer(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!container) return null;

  return ReactDOM.createPortal(
    <PiPContent
      analysis={analysis}
      isAnalyzing={isAnalyzing}
      isListening={isListening}
      callEnded={callEnded}
      inputMode={inputMode}
      meetingBotStatus={meetingBotStatus}
      onTogglePause={onTogglePause}
      onEndCall={onEndCall}
      onClose={onClose}
      prospectName={prospectName}
      companyName={companyName}
      categories={categories}
      pinnedQuestion={pinnedQuestion}
      onPinQuestion={onPinQuestion}
    />,
    container
  );
}

/**
 * Compact coaching UI rendered inside the PiP window.
 * Pure inline styles so it works even if Tailwind stylesheets fail
 * to load in the foreign document (e.g. some popup blockers).
 */
function PiPContent({
  analysis,
  isAnalyzing,
  isListening,
  callEnded,
  inputMode,
  meetingBotStatus,
  onTogglePause,
  onEndCall,
  onClose,
  prospectName,
  companyName,
  categories,
  pinnedQuestion,
  onPinQuestion,
}) {
  const overall = analysis?.overall_score ?? 0;
  const scores = analysis?.scores || {};
  const rawQuestions = (analysis?.suggested_questions || []);
  // Surface the pinned question at the top (same logic as main view)
  const pinnedText = pinnedQuestion?.question || pinnedQuestion?.text;
  const orderedQuestions = (() => {
    if (!pinnedText) return rawQuestions.slice(0, 3);
    const rest = rawQuestions.filter(q => (q.question || q.text) !== pinnedText);
    const head = rawQuestions.find(q => (q.question || q.text) === pinnedText) || pinnedQuestion;
    return [head, ...rest].slice(0, 3);
  })();
  const questions = orderedQuestions;

  const isLive = inputMode === 'meeting'
    ? ['in_meeting', 'in_call'].includes(meetingBotStatus)
    : isListening;
  const isJoiningBot = inputMode === 'meeting' && meetingBotStatus === 'joining';
  const headerLabel = callEnded
    ? 'CALL ENDED'
    : isJoiningBot
      ? 'BOT JOINING…'
      : isLive
        ? 'LIVE COACH'
        : 'PAUSED';
  const headerDotColor = callEnded
    ? '#a1a1aa'
    : isJoiningBot
      ? '#f59e0b'
      : isLive
        ? '#ef4444'
        : '#d4d4d8';
  const headerDotShadow = !callEnded && (isLive || isJoiningBot)
    ? `0 0 0 4px ${isJoiningBot ? 'rgba(245,158,11,0.18)' : 'rgba(239,68,68,0.15)'}`
    : 'none';

  const scoreColor =
    overall >= 70 ? '#16a34a' : overall >= 40 ? '#d97706' : '#dc2626';

  return (
    <div
      data-testid="pip-coach-root"
      style={{
        height: '100vh',
        width: '100vw',
        display: 'flex',
        flexDirection: 'column',
        background: '#ffffff',
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: '10px 14px',
          borderBottom: '1px solid #e4e4e7',
          background: '#fafafa',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
          <div
            style={{
              width: 8,
              height: 8,
              borderRadius: 999,
              background: headerDotColor,
              boxShadow: headerDotShadow,
              flexShrink: 0,
              animation: isJoiningBot ? 'pip-pulse 1.4s ease-in-out infinite' : undefined,
            }}
          />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: 0.5 }}>
              {headerLabel}
            </div>
            <div
              style={{
                fontSize: 11,
                color: '#71717a',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: 220,
              }}
            >
              {prospectName || 'Prospect'}{companyName ? ` · ${companyName}` : ''}
            </div>
          </div>
        </div>
        <button
          onClick={onClose}
          data-testid="pip-close-btn"
          style={{
            background: 'transparent',
            border: '1px solid #e4e4e7',
            borderRadius: 6,
            padding: '4px 8px',
            fontSize: 11,
            color: '#52525b',
            cursor: 'pointer',
          }}
        >
          Close
        </button>
      </div>

      {/* Overall Score + Category mini-scores */}
      <div
        style={{
          padding: '12px 14px',
          borderBottom: '1px solid #e4e4e7',
          display: 'flex',
          alignItems: 'center',
          gap: 14,
        }}
      >
        <div
          style={{
            width: 56,
            height: 56,
            borderRadius: 999,
            background: `${scoreColor}1a`,
            border: `2px solid ${scoreColor}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
          data-testid="pip-overall-score"
        >
          <span style={{ fontSize: 18, fontWeight: 700, color: scoreColor }}>
            {overall}
          </span>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: 1,
              color: '#71717a',
              textTransform: 'uppercase',
              marginBottom: 6,
            }}
          >
            Qualification
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {(categories || []).slice(0, 4).map((cat) => {
              const key = (cat.name || cat).toString();
              // Try multiple casings to match backend output
              const val =
                scores[key] ??
                scores[key.toLowerCase()] ??
                scores[key.toUpperCase()] ??
                0;
              const barColor = val >= 70 ? '#16a34a' : val >= 40 ? '#d97706' : '#a1a1aa';
              return (
                <div
                  key={key}
                  style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                  data-testid={`pip-score-${key.toLowerCase()}`}
                >
                  <span
                    style={{
                      fontSize: 10,
                      color: '#52525b',
                      width: 56,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {key}
                  </span>
                  <div
                    style={{
                      flex: 1,
                      height: 4,
                      background: '#f4f4f5',
                      borderRadius: 999,
                      overflow: 'hidden',
                    }}
                  >
                    <div
                      style={{
                        width: `${val}%`,
                        height: '100%',
                        background: barColor,
                        transition: 'width 400ms ease',
                      }}
                    />
                  </div>
                  <span style={{ fontSize: 10, fontWeight: 600, width: 22, textAlign: 'right' }}>
                    {val}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Suggested Questions */}
      <div
        style={{
          flex: 1,
          padding: '12px 14px',
          overflowY: 'auto',
          background: '#ffffff',
        }}
        data-testid="pip-questions"
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 8,
          }}
        >
          <span
            style={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: 1.5,
              color: '#71717a',
              textTransform: 'uppercase',
            }}
          >
            Ask Next
          </span>
          {isAnalyzing && (
            <span
              style={{
                fontSize: 10,
                color: '#0E4E5A',
                background: '#F0F8F9',
                padding: '2px 6px',
                borderRadius: 999,
                fontWeight: 600,
              }}
              data-testid="pip-analyzing-indicator"
            >
              Analyzing…
            </span>
          )}
        </div>

        {callEnded ? (
          <div
            style={{
              padding: 18,
              textAlign: 'center',
              color: '#71717a',
              fontSize: 13,
              border: '1px dashed #e4e4e7',
              borderRadius: 8,
            }}
            data-testid="pip-call-ended-state"
          >
            <div
              style={{
                width: 44,
                height: 44,
                margin: '0 auto 10px',
                borderRadius: 999,
                background: '#dcfce7',
                color: '#16a34a',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              aria-hidden="true"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
            </div>
            <div style={{ fontWeight: 600, color: '#27272a', marginBottom: 4 }}>
              Call ended
            </div>
            <div>Final score: {overall}</div>
            <button
              onClick={onClose}
              data-testid="pip-final-close-btn"
              style={{
                marginTop: 14,
                background: '#18181b',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                padding: '8px 14px',
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Close Coach
            </button>
          </div>
        ) : questions.length === 0 ? (
          <div
            style={{
              padding: 14,
              border: '1px dashed #e4e4e7',
              borderRadius: 8,
              color: '#a1a1aa',
              fontSize: 12,
              textAlign: 'center',
            }}
          >
            Waiting for conversation… questions will appear here.
          </div>
        ) : (
          <ol style={{ margin: 0, padding: 0, listStyle: 'none' }}>
            {questions.map((q, i) => {
              const text = typeof q === 'string' ? q : (q.question || q.text || '');
              const category = typeof q === 'string' ? null : (q.category || q.gap_area);
              const isPinned = pinnedText && text === pinnedText;
              const pinTopColor = isPinned ? '#b45309' : (i === 0 ? '#08383F' : '#71717a');
              const pinTopBg = isPinned ? '#fde68a' : (i === 0 ? '#E6F3F5' : '#f4f4f5');
              return (
                <li
                  key={i}
                  data-testid={`pip-question-${i}`}
                  style={{
                    padding: '10px 12px',
                    border: '1px solid',
                    borderRadius: 8,
                    marginBottom: 8,
                    background: isPinned ? '#fffbeb' : (i === 0 ? '#F0F8F9' : '#fafafa'),
                    borderColor: isPinned ? '#fcd34d' : (i === 0 ? '#7CD9CC' : '#e4e4e7'),
                    position: 'relative',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      marginBottom: 4,
                    }}
                  >
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        color: pinTopColor,
                        background: pinTopBg,
                        padding: '1px 6px',
                        borderRadius: 999,
                      }}
                    >
                      {isPinned ? '📌 Pinned' : (i === 0 ? 'Top' : `#${i + 1}`)}
                    </span>
                    {category && (
                      <span
                        style={{
                          fontSize: 10,
                          color: '#71717a',
                          textTransform: 'uppercase',
                          letterSpacing: 0.5,
                        }}
                      >
                        {category}
                      </span>
                    )}
                    {onPinQuestion && (
                      <button
                        type="button"
                        onClick={() => onPinQuestion(isPinned ? null : (typeof q === 'string' ? { question: q } : q))}
                        title={isPinned ? 'Unpin question' : 'Pin to top'}
                        data-testid={`pip-pin-btn-${i}`}
                        style={{
                          marginLeft: 'auto',
                          background: isPinned ? '#fde68a' : 'transparent',
                          border: '1px solid',
                          borderColor: isPinned ? '#f59e0b' : '#e4e4e7',
                          borderRadius: 6,
                          padding: '2px 6px',
                          fontSize: 11,
                          fontWeight: 600,
                          color: isPinned ? '#92400e' : '#52525b',
                          cursor: 'pointer',
                          lineHeight: 1,
                        }}
                      >
                        {isPinned ? 'Unpin' : 'Pin'}
                      </button>
                    )}
                  </div>
                  <div style={{ fontSize: 13, lineHeight: 1.4, color: '#18181b' }}>
                    {text}
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </div>

      {/* Action buttons */}
      {!callEnded && (
        <div
          style={{
            padding: '10px 14px',
            borderTop: '1px solid #e4e4e7',
            display: 'flex',
            gap: 8,
            background: '#fafafa',
          }}
        >
          {inputMode !== 'meeting' && (
            <button
              onClick={onTogglePause}
              data-testid="pip-pause-btn"
              style={{
                flex: 1,
                background: isListening ? '#fff' : '#18181b',
                color: isListening ? '#18181b' : '#fff',
                border: '1px solid #18181b',
                borderRadius: 6,
                padding: '8px 12px',
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              {isListening ? 'Pause' : 'Resume'}
            </button>
          )}
          <button
            onClick={onEndCall}
            data-testid="pip-end-call-btn"
            style={{
              flex: 1,
              background: '#fff',
              color: '#dc2626',
              border: '1px solid #fecaca',
              borderRadius: 6,
              padding: '8px 12px',
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            End Call
          </button>
        </div>
      )}
    </div>
  );
}

export default PiPCoachingWindow;
